import { useState, useEffect, useCallback, useContext, type PropsWithChildren } from "react";
import { useSessionTimeout, SessionTimeoutConfig } from "../hooks/useSessionTimeout";
import { SessionTimeoutWarning } from "./SessionTimeoutWarning";
import { SessionLoggedOutModal } from "./SessionLoggedOutModal";
import { lazy, Suspense } from "react";

const SessionTimeoutTestingInterface = lazy(() =>
    import("./SessionTimeoutTestingInterface").then(module => ({
        default: module.SessionTimeoutTestingInterface
    }))
);
import { SessionContext } from "../utility/useSession";

export const SessionTimeoutProvider = (props: PropsWithChildren) => {
    const { current } = useContext(SessionContext);

    const userTimeoutHours = current?.site?.preferences?.session_timeout_duration || 24;
    const userTimeoutMinutes = userTimeoutHours * 60;

    const [config, setConfig] = useState<SessionTimeoutConfig>({
        timeoutMinutes: userTimeoutMinutes,
        warningMinutes: 2
    });

    // Update config when user preferences change
    useEffect(() => {
        setConfig(prev => ({
            ...prev,
            timeoutMinutes: userTimeoutMinutes
        }));
    }, [userTimeoutMinutes]);

    const updateConfig = useCallback((newConfig: Partial<SessionTimeoutConfig>) => {
        setConfig(prev => ({ ...prev, ...newConfig }));
    }, []);

    const session = useSessionTimeout(config, updateConfig);
    const [showLoggedOutModal, setShowLoggedOutModal] = useState(false);

    // Auto-logout and show modal when session expires
    useEffect(() => {
        if (session.isExpired && !showLoggedOutModal) {
            setShowLoggedOutModal(true);
            session.logout();
        }
    }, [session.isExpired, session.logout, showLoggedOutModal]);

    const handleCloseLoggedOutModal = useCallback(() => {
        setShowLoggedOutModal(false);
    }, []);

    return (
        <>
            {props.children}

            {session.isEnabled && session.isTestingEnabled && localStorage.getItem("uptonogood") === "true" && (
                <Suspense fallback={null}>
                    <SessionTimeoutTestingInterface updateConfig={updateConfig} session={session} />
                </Suspense>
            )}

            {(session.isWarning && !session.isExpired) && (
                <SessionTimeoutWarning
                    onExtendSession={session.extend}
                    onLogout={session.logout}
                    isWarning={true}
                    timeRemaining={session.timeRemaining || 15000}
                />
            )}

            <SessionLoggedOutModal
                isOpen={showLoggedOutModal}
                onCancel={handleCloseLoggedOutModal}
            />
        </>
    );
};
